import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from "recharts";
import {
  AlertTriangle, ArrowDown, ArrowUp, Download, Info, Loader2, Minus, RefreshCw,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const PRESETS = [
  ["this_month", "This month"], ["last_month", "Last month"],
  ["last_3_months", "Last 3 months"], ["last_6_months", "Last 6 months"],
  ["last_12_months", "Last 12 months"],
  ["this_calendar_year", "This calendar year"],
  ["last_calendar_year", "Last calendar year"],
  ["this_financial_year", "This financial year"],
  ["last_financial_year", "Last financial year"],
  ["custom", "Custom range"],
];
const COMPARES = [
  ["previous_period", "Previous equivalent period"],
  ["last_year", "Same period last year"],
  ["none", "No comparison"],
];
const TABS = ["Overview", "Bookings & Revenue", "Laboratory", "Water Quality",
  "Partners & Opportunities", "Retests", "Kits & Fulfilment"];

const money = (v) => v == null ? "—"
  : v.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
const num = (v) => v == null ? "—" : v.toLocaleString("en-AU");
const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1)
    .toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
};
const fmt = (v, f) => f === "currency" ? money(v) : f === "days"
  ? (v == null ? "—" : `${v} d`) : num(v);

export default function AdminAnalytics() {
  const [preset, setPreset] = useState("last_12_months");
  const [compare, setCompare] = useState("previous_period");
  const [range, setRange] = useState({ start: "", end: "" });
  const [tab, setTab] = useState("Overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [series, setSeries] = useState({ paid_bookings: true, gross_revenue: true, tests_completed: false });
  const [pkgMode, setPkgMode] = useState("bookings");

  const load = useCallback(async () => {
    if (preset === "custom" && (!range.start || !range.end)) return;
    setLoading(true); setErr("");
    try {
      const { data: d } = await api.get("/admin/analytics/monthly", {
        params: { preset, compare, start: range.start, end: range.end },
      });
      setData(d);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load analytics");
    } finally { setLoading(false); }
  }, [preset, compare, range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const chartRows = useMemo(() => (data?.months || []).map((m) => ({
    month: monthLabel(m), key: m,
    paid_bookings: data.series.paid_bookings[m],
    gross_revenue: data.series.gross_revenue[m],
    tests_received: data.series.tests_received[m],
    tests_completed: data.series.tests_completed[m],
    reports_published: data.series.reports_published[m],
  })), [data]);

  const pkgRows = useMemo(() => {
    if (!data) return [];
    const src = data.packages[pkgMode];
    return data.months.map((m) => ({
      month: monthLabel(m),
      ...Object.fromEntries(Object.keys(src).map((p) => [p, src[p][m]])),
    }));
  }, [data, pkgMode]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/analytics/monthly.csv", {
        params: { preset, compare, start: range.start, end: range.end },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `aware-monthly-${preset}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="admin-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Monthly Performance</h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="analytics-period-label">
            {data ? `${data.period.label} · reporting in ${data.timezone}` : "Loading…"}
            {data?.comparison && ` · vs ${data.comparison.label}`}
          </p>
        </div>
        <Link to="/admin" className="text-sm font-semibold text-cyan-700 hover:text-cyan-900"
          data-testid="analytics-back-dashboard">← Admin dashboard</Link>
      </div>

      {/* Controls */}
      <div className="mt-5 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3"
        data-testid="analytics-controls">
        <select value={preset} onChange={(e) => setPreset(e.target.value)}
          data-testid="analytics-preset-select"
          className="h-9 rounded-full border border-slate-300 px-3 text-sm">
          {PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {preset === "custom" && (
          <>
            <input type="date" value={range.start} data-testid="analytics-custom-start"
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="h-9 rounded-full border border-slate-300 px-3 text-sm" />
            <input type="date" value={range.end} data-testid="analytics-custom-end"
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="h-9 rounded-full border border-slate-300 px-3 text-sm" />
          </>
        )}
        <select value={compare} onChange={(e) => setCompare(e.target.value)}
          data-testid="analytics-compare-select"
          className="h-9 rounded-full border border-slate-300 px-3 text-sm">
          {COMPARES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Button size="sm" variant="outline" className="rounded-full h-9" onClick={load}
          data-testid="analytics-refresh" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
        <Button size="sm" variant="outline" className="rounded-full h-9 ml-auto"
          onClick={exportCsv} data-testid="analytics-export-csv">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
        {data && <span className="text-[11px] text-slate-400" data-testid="analytics-query-ms">
          {data.query_ms} ms{data.cached ? " · cached" : ""}</span>}
      </div>

      {/* Tabs */}
      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-slate-200" data-testid="analytics-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`analytics-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`whitespace-nowrap px-3.5 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === t ? "border-cyan-600 text-cyan-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t}
          </button>
        ))}
      </div>

      {err && (
        <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"
          data-testid="analytics-error">{err}</div>
      )}
      {loading && !data && (
        <div className="mt-10 flex items-center gap-2 text-sm text-slate-500" data-testid="analytics-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Aggregating…
        </div>
      )}

      {data && (tab === "Overview" || tab === "Bookings & Revenue") && (
        <>
          {tab === "Overview" && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="analytics-kpi-cards">
              {data.cards.map((c) => <KpiCard key={c.key} card={c} months={data.months} />)}
            </div>
          )}

          <Panel title="Bookings and revenue by month" testId="analytics-bookings-chart"
            right={
              <div className="flex flex-wrap gap-2 text-xs">
                {[["paid_bookings", "Paid bookings"], ["gross_revenue", "Gross revenue"],
                  ["tests_completed", "Tests completed"]].map(([k, l]) => (
                  <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={!!series[k]} data-testid={`analytics-series-${k}`}
                      onChange={(e) => setSeries((s) => ({ ...s, [k]: e.target.checked }))} />
                    {l}
                  </label>
                ))}
              </div>
            }>
            {data.totals.paid_bookings === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
                  <RTooltip content={<MonthTooltip data={data} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {series.paid_bookings && <Bar yAxisId="l" dataKey="paid_bookings" name="Paid bookings" fill="#0891b2" radius={[4, 4, 0, 0]} />}
                  {series.gross_revenue && <Line yAxisId="r" dataKey="gross_revenue" name="Gross revenue (AUD)" stroke="#0f172a" strokeWidth={2} dot={false} />}
                  {series.tests_completed && <Line yAxisId="l" dataKey="tests_completed" name="Tests completed" stroke="#16a34a" strokeWidth={2} dot={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel title="Test package breakdown" testId="analytics-package-chart"
            right={
              <div className="flex gap-1 text-xs">
                {[["bookings", "Paid bookings"], ["revenue", "Revenue"]].map(([v, l]) => (
                  <button key={v} onClick={() => setPkgMode(v)} data-testid={`analytics-package-mode-${v}`}
                    className={`rounded-full px-3 py-1 font-semibold ${pkgMode === v
                      ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
                ))}
              </div>
            }>
            {data.totals.paid_bookings === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={pkgRows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v) => pkgMode === "revenue" ? money(v) : num(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {["basic", "tank", "bore", "other"].map((p, i) => (
                    <Bar key={p} dataKey={p} stackId="p" name={data.packages.labels[p]}
                      fill={["#0891b2", "#0f766e", "#7c2d12", "#94a3b8"][i]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel title="Booking source by month" testId="analytics-sources">
            <SourceTable data={data} />
          </Panel>

          <LabPanels data={data} />
          <MonthlyTable data={data} />
          <GapsPanel gaps={data.data_gaps} />
        </>
      )}

      {data && tab === "Laboratory" && (
        <>
          <LabPanels data={data} />
          <MonthlyTable data={data} />
        </>
      )}

      {data && !["Overview", "Bookings & Revenue", "Laboratory"].includes(tab) && (
        <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center"
          data-testid="analytics-tab-pending">
          <p className="text-sm font-semibold text-slate-700">{tab}</p>
          <p className="mt-1 text-sm text-slate-500">
            This section is scheduled for the next phase. Nothing is shown here rather
            than showing placeholder numbers.
          </p>
        </div>
      )}
    </div>
  );
}

const Panel = ({ title, right, children, testId }) => (
  <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-base font-bold text-slate-900">{title}</h2>
      {right}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const Empty = () => (
  <div className="py-10 text-center text-sm text-slate-500" data-testid="analytics-empty">
    No records in this period.
  </div>
);

function KpiCard({ card, months }) {
  const { change, direction } = card;
  const good = direction === "up_is_good" ? (change.absolute || 0) > 0
    : direction === "down_is_good" ? (change.absolute || 0) < 0 : null;
  const tone = change.percent == null ? "text-slate-400"
    : good ? "text-emerald-700" : good === false ? "text-rose-700" : "text-slate-500";
  const Icon = change.absolute == null || change.absolute === 0 ? Minus
    : change.absolute > 0 ? ArrowUp : ArrowDown;
  const pts = (card.sparkline || []).map((v) => (typeof v === "number" ? v : 0));
  const max = Math.max(1, ...pts);
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {card.label}
        </div>
        <span title={card.tooltip} data-testid={`analytics-card-tip-${card.key}`}>
          <Info className="w-3.5 h-3.5 text-slate-300 hover:text-slate-500" />
        </span>
      </div>
      <div className="mt-1.5 text-2xl font-bold text-slate-900" data-testid={`analytics-card-value-${card.key}`}>
        {fmt(card.value, card.format)}
      </div>
      <div className={`mt-1 flex items-center gap-1 text-[11px] font-semibold ${tone}`}
        data-testid={`analytics-card-change-${card.key}`}>
        <Icon className="w-3 h-3" />
        {change.note ? change.note
          : `${change.percent > 0 ? "+" : ""}${change.percent}% vs comparison`}
      </div>
      {card.latest_month && (
        <div className="mt-0.5 text-[11px] text-slate-400">
          {monthLabel(card.latest_month)}: {fmt(card.latest_month_value, card.format)}
        </div>
      )}
      {pts.length > 1 && (
        <div className="mt-2 flex h-6 items-end gap-[2px]">
          {pts.map((v, i) => (
            <div key={months[i] || i} className="flex-1 rounded-sm bg-cyan-200"
              style={{ height: `${Math.max(4, (v / max) * 100)}%` }} />
          ))}
        </div>
      )}
    </>
  );
  return card.drilldown ? (
    <Link to={card.drilldown} data-testid={`analytics-card-${card.key}`}
      className="rounded-2xl border border-slate-200 bg-white p-3.5 hover:border-cyan-300 hover:shadow-sm transition">
      {body}
    </Link>
  ) : (
    <div className="rounded-2xl border border-slate-200 bg-white p-3.5" data-testid={`analytics-card-${card.key}`}>
      {body}
    </div>
  );
}

function MonthTooltip({ active, payload, data }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const rows = [
    ["Paid bookings", num(row.paid_bookings)],
    ["Gross revenue", money(row.gross_revenue)],
    ["Refunds", "Not captured"],
    ["Net revenue", money(row.gross_revenue)],
    ["Tests received", num(row.tests_received)],
    ["Tests completed", num(row.tests_completed)],
    ["Reports published", num(row.reports_published)],
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs shadow-lg">
      <div className="font-bold text-slate-900">{row.month}</div>
      {rows.map(([l, v]) => (
        <div key={l} className="mt-0.5 flex justify-between gap-6">
          <span className="text-slate-500">{l}</span><span className="font-semibold">{v}</span>
        </div>
      ))}
    </div>
  );
}

function SourceTable({ data }) {
  const keys = Object.keys(data.sources.by_month);
  if (!keys.length) return <Empty />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500">
          <th className="py-1.5 pr-3">Source</th>
          {data.months.map((m) => <th key={m} className="py-1.5 px-2 text-right">{monthLabel(m)}</th>)}
          <th className="py-1.5 pl-2 text-right">Total</th>
        </tr></thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k} className="border-t border-slate-100" data-testid={`analytics-source-row-${k}`}>
              <td className="py-1.5 pr-3 font-semibold text-slate-800">
                {data.sources.labels[k] || k}
              </td>
              {data.months.map((m) => (
                <td key={m} className="py-1.5 px-2 text-right tabular-nums">
                  {data.sources.by_month[k][m] || "—"}
                </td>
              ))}
              <td className="py-1.5 pl-2 text-right font-bold tabular-nums">
                {Object.values(data.sources.by_month[k]).reduce((a, b) => a + b, 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LabPanels({ data }) {
  const w = data.laboratory.workload;
  const stages = [["pending_sample", "Pending sample", "/admin/lab?status=pending_sample"],
    ["received", "Received", "/admin/lab?status=received"],
    ["results_entered", "Results entered", "/admin/lab?status=results_entered"],
    ["awaiting_review", "Awaiting review", "/admin/lab?status=awaiting_review"],
    ["approved", "Approved, not published", "/admin/lab?status=approved"],
    ["published", "Published", "/admin/lab?status=published"]];
  const t = data.laboratory;
  const spans = [["book_to_received", "Booking → sample received"],
    ["received_to_results", "Received → results entered"],
    ["received_to_published", "Received → report published"],
    ["book_to_published", "Booking → report published"]];
  return (
    <>
      <Panel title="Laboratory workload (live)" testId="analytics-lab-workload">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {stages.map(([k, l, href]) => (
            <Link key={k} to={href} data-testid={`analytics-workload-${k}`}
              className="rounded-xl border border-slate-200 p-3 hover:border-cyan-300 transition">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
              <div className="mt-1 text-xl font-bold text-slate-900">{num(w[k] ?? 0)}</div>
            </Link>
          ))}
        </div>
      </Panel>
      <Panel title="Turnaround time (medians)" testId="analytics-turnaround">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {spans.map(([k, l]) => (
            <div key={k} className="rounded-xl border border-slate-200 p-3" data-testid={`analytics-turnaround-${k}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
              <div className="mt-1 text-xl font-bold text-slate-900">
                {t.turnaround_medians[k] == null ? "—" : `${t.turnaround_medians[k]} d`}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-400">
                mean {t.turnaround_means[k] ?? "—"} · n={t.turnaround_sample_sizes[k]}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          Calendar days (not business days), median of records where BOTH timestamps
          exist — incomplete records are excluded, never counted as zero. {t.target_note}
        </p>
      </Panel>
    </>
  );
}

function MonthlyTable({ data }) {
  const [sort, setSort] = useState({ key: "month", dir: "asc" });
  const cols = [["month", "Month"], ["paid_bookings", "Paid bookings"],
    ["gross_revenue", "Gross revenue"], ["refunds", "Refunds"],
    ["net_revenue", "Net revenue"], ["tests_received", "Tests received"],
    ["tests_completed", "Tests completed"], ["reports_published", "Reports published"],
    ["median_turnaround_days", "Median turnaround"], ["avg_health_score", "Avg WHS"],
    ["health_flagged", "Health-flagged"], ["quote_opportunities", "Quote opps"],
    ["retests_due", "Retests due"]];
  const rows = useMemo(() => {
    const r = [...data.table];
    r.sort((a, b) => {
      const x = a[sort.key], y = b[sort.key];
      if (x == null) return 1;
      if (y == null) return -1;
      return (x > y ? 1 : x < y ? -1 : 0) * (sort.dir === "asc" ? 1 : -1);
    });
    return r;
  }, [data.table, sort]);
  const money2 = (k, v) => ["gross_revenue", "net_revenue", "refunds"].includes(k)
    ? (v == null ? "Not captured" : money(v))
    : k === "median_turnaround_days" ? (v == null ? "—" : `${v} d`) : num(v);
  return (
    <Panel title="Monthly summary" testId="analytics-monthly-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500">
            {cols.map(([k, l]) => (
              <th key={k} className="py-1.5 px-2 whitespace-nowrap cursor-pointer select-none"
                data-testid={`analytics-table-sort-${k}`}
                onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))}>
                {l}{sort.key === k ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month} className="border-t border-slate-100 hover:bg-slate-50"
                data-testid={`analytics-table-row-${r.month}`}>
                {cols.map(([k]) => (
                  <td key={k} className="py-1.5 px-2 whitespace-nowrap tabular-nums">
                    {k === "month" ? (
                      <Link to={`/admin/bookings?payment_status=paid&month=${r.month}`}
                        className="font-semibold text-cyan-800 hover:underline">
                        {monthLabel(r.month)}
                      </Link>
                    ) : money2(k, r[k])}
                    {k === "paid_bookings" && r.mom_bookings_change?.percent != null && (
                      <span className={`ml-1 text-[10px] ${r.mom_bookings_change.percent >= 0
                        ? "text-emerald-700" : "text-rose-700"}`}>
                        {r.mom_bookings_change.percent > 0 ? "+" : ""}{r.mom_bookings_change.percent}%
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold" data-testid="analytics-table-totals">
              <td className="py-2 px-2">Total</td>
              <td className="py-2 px-2 tabular-nums">{num(data.totals.paid_bookings)}</td>
              <td className="py-2 px-2 tabular-nums">{money(data.totals.gross_revenue)}</td>
              <td className="py-2 px-2">Not captured</td>
              <td className="py-2 px-2 tabular-nums">{money(data.totals.net_revenue)}</td>
              <td className="py-2 px-2 tabular-nums">{num(data.totals.tests_received)}</td>
              <td className="py-2 px-2 tabular-nums">{num(data.totals.tests_completed)}</td>
              <td className="py-2 px-2 tabular-nums">{num(data.totals.reports_published)}</td>
              <td className="py-2 px-2 tabular-nums">
                {data.totals.median_turnaround_days == null ? "—" : `${data.totals.median_turnaround_days} d`}</td>
              <td className="py-2 px-2 tabular-nums">{data.totals.avg_health_score ?? "—"}</td>
              <td className="py-2 px-2 tabular-nums">{num(data.totals.health_flagged)}</td>
              <td className="py-2 px-2 tabular-nums">{num(data.totals.quote_opportunities)}</td>
              <td className="py-2 px-2 tabular-nums">{num(data.totals.retests_due)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

const GapsPanel = ({ gaps }) => (
  <Panel title="Known data gaps" testId="analytics-data-gaps">
    <ul className="space-y-2.5">
      {gaps.map((g) => (
        <li key={g.metric} className="flex gap-2.5 text-xs" data-testid={`analytics-gap-${g.status}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <div>
            <div className="font-bold text-slate-900">{g.metric}
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                {g.status.replace(/_/g, " ")}</span>
            </div>
            <p className="mt-0.5 text-slate-600">{g.detail}</p>
            <p className="mt-0.5 text-slate-500"><b>Smallest safe fix:</b> {g.smallest_safe_fix}</p>
          </div>
        </li>
      ))}
    </ul>
  </Panel>
);
